import { API_URLS } from "@solomon-apps/config";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { captureWalletEvent, eventError, eventId, type WalletEventProperties } from "../../analytics/walletEvents";
import { walletJourney } from "../../analytics/walletJourney";

// Set to true to enable location checks and country restrictions.
const GEO_RESTRICTION_ENABLED = false;

const RESTRICTED_COUNTRIES = ["US", "CU", "IR", "KP", "SY", "RU", "BY", "SD", "MM", "AF", "IQ"];

interface GeoInfoResponse {
    country: string;
    ip?: string;
    countryName?: string;
}

export interface GeoRestrictionResult {
    isRestricted: boolean;
    failed: boolean;
    isChecked: boolean;
    ip: string | undefined;
    country: string | undefined;
}

const COUNTRY_NAMES: Record<string, string> = {
    US: "United States",
    CU: "Cuba",
    IR: "Iran",
    KP: "North Korea",
    SY: "Syria",
    RU: "Russia",
    BY: "Belarus",
    SD: "Sudan",
    MM: "Myanmar",
    AF: "Afghanistan",
    IQ: "Iraq"
};

export function useGeoRestriction(): GeoRestrictionResult {
    const check = useRef<{ id: string; started: number; context: WalletEventProperties } | null>(null);
    const query = useQuery<GeoInfoResponse>({
        queryKey: ["geo-info"],
        enabled: GEO_RESTRICTION_ENABLED,
        queryFn: async ({ signal }) => {
            if (!check.current) {
                const { entry_point, connection_flow_id } = walletJourney.context();
                check.current = {
                    id: eventId(),
                    started: performance.now(),
                    context: { entry_point, connection_flow_id }
                };
                captureWalletEvent("wallet_connect:location_check_started", {
                    ...check.current.context,
                    check_id: check.current.id
                });
            }
            const response = await fetch(`${API_URLS.REWARDS}/info`, { signal });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json() as Promise<GeoInfoResponse>;
        },
        staleTime: 30 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: true,
        refetchOnReconnect: false
    });

    useEffect(() => {
        if (!GEO_RESTRICTION_ENABLED) return;
        if (!check.current || query.isFetching || (!query.isSuccess && !query.isError)) return;
        const properties = {
            ...check.current.context,
            check_id: check.current.id,
            duration_ms: performance.now() - check.current.started
        };
        check.current = null;
        if (query.isError)
            captureWalletEvent("wallet_connect:location_check_failed", { ...properties, ...eventError(query.error) });
        else
            captureWalletEvent("wallet_connect:location_check_completed", {
                ...properties,
                decision: RESTRICTED_COUNTRIES.includes(query.data.country) ? "restricted" : "allowed"
            });
    }, [query.isFetching, query.isSuccess, query.isError, query.data, query.error]);

    if (!GEO_RESTRICTION_ENABLED) {
        return {
            isRestricted: false,
            failed: false,
            isChecked: true,
            ip: undefined,
            country: undefined
        };
    }

    if (query.isError) {
        console.warn("[Geo] Failed to fetch location info, blocking access:", query.error);
    }

    const isRestricted = query.isError ? true : query.data ? RESTRICTED_COUNTRIES.includes(query.data.country) : false;
    const countryCode = query.data?.country;

    return {
        isRestricted,
        failed: query.isError,
        isChecked: query.isSuccess || query.isError,
        ip: query.data?.ip,
        country: query.data?.countryName ?? (countryCode ? COUNTRY_NAMES[countryCode] : undefined)
    };
}
